import React from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import blogPosts from "../data/blogPosts.json";
import SEO from "../components/SEO.jsx";
import PageHero from "../components/page/PageHero.jsx";

export default function Post() {
  const { slug } = useParams(); // 🔁 use slug now
  const location = useLocation();
  const fullUrl = `https://2merative.com${location.pathname}`;

  // Find by slug primarily; fallback to id for backward-compat
  const post = blogPosts.find((p) => p.slug === slug) || blogPosts.find((p) => p.id === slug);

  if (!post) {
    return (
      <div className="pt-32 px-6 max-w-3xl mx-auto text-center">
        <SEO
          title="Article not found | 2merative"
          description="The article you're looking for can't be found."
          path={`/blog/${slug || ""}`}
          image="/og-image.png"
          noindex
        />
        <h1 className="text-3xl font-bold text-blue-900 mb-4">Article not found</h1>
        <p className="mb-6 text-gray-700">Try browsing the latest posts.</p>
        <Link to="/blog" className="text-blue-600 hover:underline">Back to Blog</Link>
      </div>
    );
  }

  const { title, summary, image, date, content } = post;
  const prettyDate = new Date(date).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Australia/Adelaide",
  });

  const canonicalPath = `/blog/${post.slug || slug}`;
  const hero = image || "/og-image.png";
  const articleContent = (content || "")
    .replace(/<h1(\b[^>]*)>/gi, "<h2$1>")
    .replace(/<\/h1>/gi, "</h2>");

  return (
    <>
      <SEO
        title={`${title} | 2merative`}
        description={summary || "Web design tips for small businesses."}
        path={canonicalPath}
        image={hero}
      />

      <PageHero eyebrow="Insights" title={title} lead={prettyDate} showCtas={false} />

      <article className="max-w-3xl mx-auto px-6 pt-12 pb-16">

        {hero && (
          <img
            src={hero}
            alt={title}
            className="rounded-lg shadow mb-6 w-full object-cover max-h-[400px]"
            loading="lazy"
          />
        )}

        <div
          className="prose prose-lg max-w-none mb-12"
          dangerouslySetInnerHTML={{ __html: articleContent }}
        />

        <section className="rounded-2xl border border-blue-100 bg-blue-50 p-5 mb-12">
          <h2 className="text-xl font-semibold text-blue-900 mb-3">Related 2merative pages</h2>
          <p className="text-gray-700 mb-2">
            Explore our <Link to="/web-design-adelaide" className="text-blue-600 hover:underline">web design Adelaide service page</Link> for broader website strategy and see our <Link to="/website-redesign-adelaide" className="text-blue-600 hover:underline">website redesign options</Link> if you are comparing next steps.
          </p>
          <p className="text-gray-700">
            If you want tailored advice, you can also <Link to="/contact" className="text-blue-600 hover:underline">contact 2merative</Link>.
          </p>
        </section>

        <div className="text-center">
          <h3 className="text-lg font-semibold mb-4">Share this post:</h3>
          <div className="flex justify-center flex-wrap gap-6">
            {/* Facebook */}
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on Facebook"
              className="hover:opacity-75 transition"
            >
              <svg fill="#1877F2" height="28" viewBox="0 0 24 24" width="28">
                <path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.407.593 24 1.324 24H12.82V14.708h-3.54V11.08h3.54V8.392c0-3.507 2.143-5.418 5.274-5.418 1.498 0 2.787.111 3.164.161v3.67l-2.172.001c-1.703 0-2.034.81-2.034 1.996v2.618h4.066l-.53 3.628h-3.536V24h6.933c.73 0 1.323-.593 1.323-1.324V1.324C24 .593 23.407 0 22.676 0z" />
              </svg>
            </a>

            {/* LinkedIn */}
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on LinkedIn"
              className="hover:opacity-75 transition"
            >
              <svg fill="#0077B5" height="28" viewBox="0 0 24 24" width="28">
                <path d="M22.23 0H1.77C.79 0 0 .774 0 1.729v20.542C0 23.225.79 24 1.77 24h20.46c.978 0 1.77-.775 1.77-1.729V1.729C24 .774 23.208 0 22.23 0zM7.12 20.452H3.556V9h3.564v11.452zM5.337 7.577c-1.14 0-1.87-.754-1.87-1.696 0-.96.75-1.698 1.915-1.698s1.872.738 1.89 1.698c-.018.942-.73 1.696-1.935 1.696zm15.114 12.875h-3.562v-5.863c0-1.474-.527-2.48-1.845-2.48-.997 0-1.592.667-1.855 1.312-.096.233-.12.559-.12.885v6.146H9.513s.047-9.974 0-11.004h3.563v1.558c.473-.73 1.32-1.77 3.21-1.77 2.344 0 4.097 1.53 4.097 4.82v6.396z" />
              </svg>
            </a>

            {/* Email Share */}
            <a
              href={`mailto:?subject=${encodeURIComponent(title)}&body=Check out this article: ${encodeURIComponent(fullUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share via Email"
              className="hover:opacity-75 transition"
            >
              <svg fill="#EA4335" height="28" viewBox="0 0 512 512" width="28">
                <path d="M502.3 190.8l-192-160C298.1 22.6 276.1 16 256 16s-42.1 6.6-54.3 14.8l-192 160C4.8 196.3 0 206.4 0 217.6V464c0 26.5 21.5 48 48 48h416c26.5 0 48-21.5 48-48V217.6c0-11.2-4.8-21.3-9.7-26.8zM256 78.1L438.5 224H73.5L256 78.1zM48 432V240.3l170.7 137.4c18.3 14.7 44.3 14.7 62.6 0L464 240.3V432H48z" />
              </svg>
            </a>

            {/* Instagram Follow */}
            <a
              href="https://www.instagram.com/2merative"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow on Instagram"
              className="hover:opacity-75 transition"
            >
              <svg fill="#E1306C" height="28" viewBox="0 0 24 24" width="28">
                <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2zm0-2A7.75 7.75 0 0 0 0 7.75v8.5A7.75 7.75 0 0 0 7.75 24h8.5A7.75 7.75 0 0 0 24 16.25v-8.5A7.75 7.75 0 0 0 16.25 0h-8.5zM12 7.5a4.5 4.5 0 1 1 0 9a4.5 4.5 0 0 1 0-9zm5.25-.5a1 1 0 1 1 0 2a1 1 0 0 1 0-2z" />
              </svg>
            </a>
          </div>
        </div>
      </article>

      {/* JSON-LD Article schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description: summary || "Web design tips for small businesses.",
            image: [`https://2merative.com${hero}`],
            datePublished: date,
            dateModified: date,
            author: { "@type": "Organization", name: "2merative" },
            publisher: {
              "@type": "Organization",
              name: "2merative",
              logo: {
                "@type": "ImageObject",
                url: "https://2merative.com/favicon-2merative-512.png",
              },
            },
            mainEntityOfPage: `https://2merative.com${canonicalPath}`,
          }),
        }}
      />
    </>
  );
}
